import React from 'react';

import classNames from 'classnames';
import { LoadingIcon } from 'fluxo-ui/icons';

interface LoadingProps {
    size?: 'sm' | 'md' | 'lg';
    variant?: 'spinner' | 'skeleton' | 'dots';
    className?: string;
}

const Loading: React.FC<LoadingProps> = ({ size = 'md', variant = 'spinner', className = '' }) => {
    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-6 h-6',
        lg: 'w-8 h-8',
    };

    if (variant === 'spinner') {
        return (
            <div className={classNames('flex items-center justify-center', className)}>
                <LoadingIcon className={classNames(sizeClasses[size], 'animate-spin text-teal-600 dark:text-teal-400')} />
            </div>
        );
    }

    if (variant === 'dots') {
        const dotSizeClasses = {
            sm: 'w-1 h-1',
            md: 'w-2 h-2',
            lg: 'w-3 h-3',
        };

        return (
            <div className={classNames('flex items-center justify-center gap-1', className)}>
                <div
                    className={classNames(dotSizeClasses[size], 'bg-teal-600 dark:bg-teal-400 rounded-full animate-bounce')}
                    style={{ animationDelay: '0ms' }}
                ></div>
                <div
                    className={classNames(dotSizeClasses[size], 'bg-teal-600 dark:bg-teal-400 rounded-full animate-bounce')}
                    style={{ animationDelay: '150ms' }}
                ></div>
                <div
                    className={classNames(dotSizeClasses[size], 'bg-teal-600 dark:bg-teal-400 rounded-full animate-bounce')}
                    style={{ animationDelay: '300ms' }}
                ></div>
            </div>
        );
    }

    if (variant === 'skeleton') {
        return (
            <div className={classNames('animate-pulse', className)}>
                <div className="space-y-3">
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-lg w-3/4"></div>
                    <div className="space-y-2">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-lg w-5/6"></div>
                    </div>
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-lg w-1/2"></div>
                </div>
            </div>
        );
    }

    return null;
};

export const LoadingSpinner: React.FC<Omit<LoadingProps, 'variant'>> = (props) => <Loading {...props} variant="spinner" />;

export const LoadingDots: React.FC<Omit<LoadingProps, 'variant'>> = (props) => <Loading {...props} variant="dots" />;

export const LoadingSkeleton: React.FC<Omit<LoadingProps, 'variant'>> = (props) => <Loading {...props} variant="skeleton" />;

export const InlineLoading: React.FC<{ text?: string; size?: 'sm' | 'md' }> = ({ text = 'Loading...', size = 'sm' }) => (
    <div className="inline-flex items-center gap-2">
        <LoadingIcon className={classNames(size === 'sm' ? 'w-4 h-4' : 'w-5 h-5', 'animate-spin text-teal-600 dark:text-teal-400')} />
        <span className={classNames(size === 'sm' ? 'text-sm' : 'text-base', 'text-slate-500 dark:text-slate-400')}>{text}</span>
    </div>
);

export const BlockLoading: React.FC<{
    text?: string;
    size?: 'sm' | 'md' | 'lg';
}> = ({ text = 'Loading...', size = 'md' }) => (
    <div className="flex flex-col items-center justify-center py-12">
        <div className="relative mb-4">
            <div
                aria-hidden
                className={classNames(
                    size === 'sm' ? 'w-8 h-8' : size === 'md' ? 'w-12 h-12' : 'w-16 h-16',
                    'absolute inset-0 rounded-full bg-teal-500/10 dark:bg-teal-400/10 blur-md',
                )}
            />
            <LoadingIcon
                className={classNames(
                    size === 'sm' ? 'w-8 h-8' : size === 'md' ? 'w-12 h-12' : 'w-16 h-16',
                    'relative animate-spin text-teal-600 dark:text-teal-400',
                )}
            />
        </div>
        <p className={classNames(size === 'sm' ? 'text-sm' : size === 'md' ? 'text-base' : 'text-lg', 'text-slate-500 dark:text-slate-400')}>
            {text}
        </p>
    </div>
);

export { Loading };
